import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  User,
  Hash,
  Phone,
  Mail,
  GraduationCap,
  Layers,
  Send,
  CheckCircle2,
  Info,
  Camera,
  Upload,
  Trash2,
  X,
  Download,
  FileText,
  ChevronDown,
  Pencil,
  AlertCircle,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import logo2 from "../images/logo2.png";
import satyasailogo from "../images/satyasai.jpg";
import API_BASE_URL from "./ApiConfig";

const STREAMS = [
  "Full Stack Website Development",
  "Android Development",
  "Python Development",
  "Website with IoT",
  "Android App Development with IoT",
];

const SEMESTERS = [1, 2, 3, 4, 5, 6];

const NAME_MAX = 30;

const INITIAL = {
  name: "",
  regNo: "",
  phone: "",
  email: "",
  semester: "",
  stream: "",
  level: 0,
  photo: "",
};

const levelLabel = (n) => {
  if (!n) return "Not rated yet";
  if (n <= 3) return "Beginner";
  if (n <= 7) return "Intermediate";
  return "Advanced";
};

const DECLARATION =
  "I confirm that the details given above are correct and I wish to join the Skill Development Training Program as a student of Sri Sathya Sai Institute for Women.";

const TERMS = [
  {
    title: "Attendance and Completion",
    text: "I will attend the training sessions regularly and complete the full program.",
  },
  {
    title: "Data Consent and Privacy",
    text: "I agree that my name, registration number, phone, email and photo can be collected and stored for registration, communication and academic records. My data will not be shared with others without my consent.",
  },
  {
    title: "Discipline",
    text: "I will follow discipline and behave professionally in all sessions.",
  },
  {
    title: "Project Submission",
    text: "I must submit my final year project before the deadline.",
  },
  {
    title: "Refund",
    text: "Once the fees are paid, they will not be refunded to the student.",
  },
];

const FEES = [
  { label: "Registration fee (pay within 1 week of registering)", amount: 200 },
  { label: "First month", amount: 1000 },
  { label: "Second month", amount: 1400 },
  { label: "Third month", amount: 1400 },
];
const FEE_TOTAL = FEES.reduce((sum, f) => sum + f.amount, 0);

const validate = (v) => {
  const e = {};
  const name = v.name.trim();
  if (!name) e.name = "Enter your full name.";
  else if (name.length < 3) e.name = "Name must be at least 3 characters.";
  else if (name.length > NAME_MAX) e.name = `Name can have at most ${NAME_MAX} characters.`;
  else if (!/^[A-Za-z .]+$/.test(name)) e.name = "Use letters only.";

  if (!v.regNo.trim()) e.regNo = "Enter your registration number.";
  else if (!/^[A-Za-z0-9/-]{4,20}$/.test(v.regNo.trim()))
    e.regNo = "Use 4–20 letters or digits.";

  if (!v.phone) e.phone = "Enter your phone number.";
  else if (!/^\d{10}$/.test(v.phone)) e.phone = "Phone number must be exactly 10 digits.";
  else if (!/^[6-9]/.test(v.phone)) e.phone = "Phone number must start with 6, 7, 8 or 9.";

  if (v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim()))
    e.email = "Enter a valid email address, or leave it empty.";

  if (!v.semester) e.semester = "Select your semester.";
  if (!v.stream) e.stream = "Select a stream.";
  if (v.stream && !v.level) e.level = "Rate your knowledge from 1 to 10.";
  if (!v.photo) e.photo = "Add your photo using the camera or your device.";
  return e;
};

/* ---------- Duplicate check: phone, registration number, email (uses GET /api/students) ---------- */

const normalizePhone = (p) => String(p || "").replace(/\D/g, "").slice(-10);
const normalizeReg = (r) => String(r || "").trim().toUpperCase();
const normalizeEmail = (m) => String(m || "").trim().toLowerCase();

const DUP_MSG = {
  phone: "This phone number is already registered. A student cannot register twice.",
  regNo: "This registration number is already registered. A student cannot register twice.",
  email: "This email address is already registered. Use a different email or leave it empty.",
};

// Returns an object like { phone: "...", regNo: "..." } for every field that already exists.
// Empty object means no duplicates. Throws if the list could not be fetched.
const findDuplicates = async (v) => {
  const res = await fetch(`${API_BASE_URL}/api/students`);
  const json = await res.json();
  if (!res.ok || json.success === false) {
    throw new Error(json.message || "Could not verify details.");
  }
  const list = Array.isArray(json.data) ? json.data : [];

  const phone = normalizePhone(v.phone);
  const regNo = normalizeReg(v.regNo);
  const email = normalizeEmail(v.email); // email is optional; only checked when provided

  const dup = {};
  list.forEach((s) => {
    if (phone && normalizePhone(s.phone) === phone) dup.phone = DUP_MSG.phone;
    if (regNo && normalizeReg(s.regNo) === regNo) dup.regNo = DUP_MSG.regNo;
    if (email && normalizeEmail(s.email) === email) dup.email = DUP_MSG.email;
  });
  return dup;
};

/* ---------- Image helpers ---------- */

const toPortraitJpeg = (src, sw, sh) => {
  const tw = 480;
  const th = 640;
  const c = document.createElement("canvas");
  c.width = tw;
  c.height = th;
  const ctx = c.getContext("2d");
  const target = tw / th;
  let cw = sw;
  let ch = sh;
  if (sw / sh > target) cw = sh * target;
  else ch = sw / target;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, tw, th);
  ctx.drawImage(src, (sw - cw) / 2, (sh - ch) / 2, cw, ch, 0, 0, tw, th);
  return c.toDataURL("image/jpeg", 0.88);
};

const toDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* ---------- PDF ---------- */

const generatePdf = async (rec) => {
  const [brand, college] = await Promise.all([toDataUrl(logo2), toDataUrl(satyasailogo)]);

  const C = {
    blue: [37, 99, 235],
    blueDark: [30, 64, 175],
    navy: [15, 23, 42],
    text: [30, 41, 59],
    muted: [100, 116, 139],
    border: [203, 213, 225],
    tint: [239, 246, 255],
    tintBorder: [191, 219, 254],
    card: [248, 250, 252],
    white: [255, 255, 255],
  };
  const MM = 0.3528;

  const render = (pageH) => {
    const doc = new jsPDF({ unit: "mm", format: [210, pageH] });
    const W = 210;
    const H = pageH;
    const fill = (c) => doc.setFillColor(c[0], c[1], c[2]);
    const stroke = (c) => doc.setDrawColor(c[0], c[1], c[2]);
    const ink = (c) => doc.setTextColor(c[0], c[1], c[2]);

    const placeLogo = (img, x, y, box) => {
      const k = Math.min(box / img.w, box / img.h);
      const w = img.w * k;
      const h = img.h * k;
      doc.addImage(img.data, "PNG", x + (box - w) / 2, y + (box - h) / 2, w, h);
    };

    fill(C.blue);
    doc.rect(0, 0, W, 46, "F");
    fill(C.blueDark);
    doc.rect(0, 46, W, 2, "F");

    const plate = 26;
    const plateY = 10;

    fill(C.white);
    doc.roundedRect(14, plateY, plate, plate, 3, 3, "F");
    if (brand) placeLogo(brand, 14 + 2, plateY + 2, plate - 4);
    const textX = 14 + plate + 6;
    ink(C.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("AK Software Developers", textX, 21.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    ink([191, 219, 254]);
    doc.text("ENGINEERING TOMORROW", textX, 28.5, { charSpace: 0.8 });

    const cx = W - 14 - plate;
    fill(C.white);
    doc.roundedRect(cx, plateY, plate, plate, 3, 3, "F");
    if (college) placeLogo(college, cx + 2, plateY + 2, plate - 4);
    ink(C.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("SRI SATHYA SAI", cx - 6, 21, { align: "right" });
    doc.text("INSTITUTE FOR WOMEN", cx - 6, 27.5, { align: "right" });

    ink(C.navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Skill Development Training Program", 14, 62);
    ink(C.blue);
    doc.setFontSize(10.5);
    doc.text("Student Registration Form", 14, 69);

    let y = 84;

    const section = (title) => {
      fill(C.blue);
      doc.rect(14, y - 4.4, 1.6, 6, "F");
      ink(C.navy);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11.5);
      doc.text(title, 18.5, y);
      stroke(C.border);
      doc.setLineWidth(0.3);
      doc.line(14, y + 3, W - 14, y + 3);
      y += 10;
    };

    section("Student Details");

    const py = y;
    const pw = 40;
    const ph = 53.3;
    const plateW = pw + 4;
    const plateH = ph + 4 + 9;
    const plateX = W - 14 - plateW;

    fill(C.white);
    stroke(C.blue);
    doc.setLineWidth(0.5);
    doc.roundedRect(plateX, py, plateW, plateH, 2.5, 2.5, "FD");
    doc.addImage(rec.photo, "JPEG", plateX + 2, py + 2, pw, ph);
    ink(C.muted);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text("STUDENT PHOTO", plateX + plateW / 2, py + 2 + ph + 5.8, { align: "center", charSpace: 0.4 });

    autoTable(doc, {
      startY: py,
      margin: { left: 14, right: 14 + plateW + 8 },
      theme: "grid",
      body: [
        ["Reference no.", rec.refNo],
        ["Date", rec.date],
        ["Full name", rec.name.trim()],
        ["Registration no.", rec.regNo.trim()],
        ["Phone number", `+91 ${rec.phone}`],
        ["Email", rec.email.trim() || "Not provided"],
        ["Semester", `Semester ${rec.semester}`],
        ["Stream", rec.stream],
        ["Knowledge level", `${rec.level} / 10  (${levelLabel(rec.level)})`],
      ],
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: { top: 3.4, bottom: 3.4, left: 4, right: 3 },
        textColor: C.navy,
        lineColor: C.border,
        lineWidth: 0.25,
        fillColor: C.white,
      },
      columnStyles: {
        0: { fontStyle: "bold", fillColor: C.tint, textColor: C.blueDark, cellWidth: 36 },
        1: { cellWidth: "auto" },
      },
    });

    const afterTable = (doc.lastAutoTable && doc.lastAutoTable.finalY) || py + 90;
    y = Math.max(afterTable, py + plateH) + 14;

    section("Declaration");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.8);
    const dLine = 9.8 * 1.4 * MM;
    const decl = doc.splitTextToSize(DECLARATION, W - 28 - 12);
    const dH = 6.2 + (decl.length - 1) * dLine + 5;
    fill(C.tint);
    stroke(C.tintBorder);
    doc.setLineWidth(0.25);
    doc.roundedRect(14, y, W - 28, dH, 2, 2, "FD");
    fill(C.blue);
    doc.rect(14, y + 1.5, 1.4, dH - 3, "F");
    ink(C.text);
    doc.text(decl, 20, y + 6.2, { lineHeightFactor: 1.4 });
    y += dH + 12;

    section("Terms and Conditions");
    const tLine = 9 * 1.4 * MM;
    TERMS.forEach((t, i) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(t.text, W - 28 - 20);
      const h = 12.8 + (lines.length - 1) * tLine + 4.5;

      fill(C.card);
      stroke(C.border);
      doc.setLineWidth(0.25);
      doc.roundedRect(14, y, W - 28, h, 2, 2, "FD");

      fill(C.blue);
      doc.circle(21.5, y + 6.2, 3.3, "F");
      ink(C.white);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text(String(i + 1), 21.5, y + 7.3, { align: "center" });

      ink(C.navy);
      doc.setFontSize(9.8);
      doc.text(t.title, 28, y + 7.2);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      ink(C.text);
      doc.text(lines, 28, y + 12.8, { lineHeightFactor: 1.4 });
      y += h + 3;
    });

    y += 9;
    section(`${TERMS.length + 1}. Fee Structure`);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.2);
    const note = doc.splitTextToSize(
      `Pay the registration fee of Rs. ${FEES[0].amount} to the coordinator of the institute within 1 week of submitting this form.`,
      W - 28 - 12
    );
    const nLine = 9.2 * 1.4 * MM;
    const nH = 6.2 + (note.length - 1) * nLine + 5;
    fill([254, 243, 199]);
    stroke([252, 211, 77]);
    doc.setLineWidth(0.25);
    doc.roundedRect(14, y, W - 28, nH, 2, 2, "FD");
    fill([245, 158, 11]);
    doc.rect(14, y + 1.5, 1.4, nH - 3, "F");
    ink([120, 53, 15]);
    doc.text(note, 20, y + 6.2, { lineHeightFactor: 1.4 });
    y += nH + 6;

    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      theme: "grid",
      head: [["Fee", "Amount"]],
      body: [
        ...FEES.map((f) => [f.label, `Rs. ${f.amount}`]),
        ["Total fees", `Rs. ${FEE_TOTAL}`],
      ],
      styles: {
        font: "helvetica",
        fontSize: 9.8,
        cellPadding: { top: 3.2, bottom: 3.2, left: 4, right: 4 },
        textColor: C.navy,
        lineColor: C.border,
        lineWidth: 0.25,
      },
      headStyles: { fillColor: C.blue, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: C.card },
      columnStyles: { 1: { halign: "right", cellWidth: 42 } },
      didParseCell: (d) => {
        if (d.section === "body" && d.row.index === FEES.length) {
          d.cell.styles.fontStyle = "bold";
          d.cell.styles.fillColor = [219, 234, 254];
          d.cell.styles.textColor = C.blueDark;
        }
      },
    });

    const endY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y;

    fill(C.blueDark);
    doc.rect(0, H - 14, W, 14, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    ink([219, 234, 254]);
    doc.text(
      `AK Software Developers  |  Engineering Tomorrow  |  Generated on ${rec.date}`,
      W / 2,
      H - 5.5,
      { align: "center" }
    );

    return { doc, endY };
  };

  const measured = render(2000);
  const pageH = Math.max(297, Math.ceil(measured.endY + 12 + 14));
  const { doc } = render(pageH);

  doc.save(`Registration_${rec.regNo.replace(/[^A-Za-z0-9]/g, "")}.pdf`);
};

/* ---------- Component ---------- */

export default function SatyaSaiReg() {
  const [values, setValues] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [record, setRecord] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);

  const errors = validate(values);
  // Returns the message to display (client validation or server-side duplicate check)
  const show = (field) => ((touched[field] || submitted) && errors[field]) || serverErrors[field] || "";

  const busy = saving || checking;

  const setField = (field, value) => setValues((p) => ({ ...p, [field]: value }));
  const blur = (field) => setTouched((p) => ({ ...p, [field]: true }));

  const handleStream = (e) => {
    setValues((p) => ({ ...p, stream: e.target.value, level: 0 }));
  };

  /* Camera */
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraOpen]);

  const openCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (captureInputRef.current) captureInputRef.current.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch (err) {
      toast.info("Camera is blocked or unavailable. Choose a photo from your device instead.");
    }
  };

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setField("photo", toPortraitJpeg(v, v.videoWidth, v.videoHeight));
    blur("photo");
    closeCamera();
  };

  /* Gallery / native camera file */
  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo must be smaller than 10 MB.");
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setField("photo", toPortraitJpeg(img, img.naturalWidth, img.naturalHeight));
      blur("photo");
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      toast.error("Could not read that image. Try another one.");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  /* Name: letters, spaces and dots only, maximum 30 characters */
  const handleName = (e) => {
    const clean = e.target.value.replace(/[^A-Za-z .]/g, "").slice(0, NAME_MAX);
    setField("name", clean);
  };

  /* Clears an earlier "already registered" error for a field once the student edits it */
  const clearServerError = (field) => {
    if (serverErrors[field]) setServerErrors((p) => ({ ...p, [field]: "" }));
  };

  /* Shows duplicate errors; returns true if any duplicate was found */
  const applyDuplicates = (dup) => {
    if (!Object.keys(dup).length) return false;
    setServerErrors((p) => ({ ...p, ...dup }));
    setTouched((p) => ({ ...p, phone: true, regNo: true, email: true }));
    toast.error(Object.values(dup)[0]);
    return true;
  };

  /* Phone: digits only */
  const handlePhone = (e) => {
    setField("phone", e.target.value.replace(/\D/g, "").slice(0, 10));
    clearServerError("phone");
  };

  /* Step 1: Validate, check the phone number against GET /api/students, then open confirmation popup */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setTouched({
      name: true,
      regNo: true,
      phone: true,
      email: true,
      semester: true,
      stream: true,
      level: true,
      photo: true,
    });
    if (Object.keys(errors).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setChecking(true);
    try {
      const dup = await findDuplicates(values);
      if (applyDuplicates(dup)) return;
      setConfirmOpen(true);
    } catch (err) {
      console.error(err);
      toast.error("Could not verify your details right now. Please check your connection and try again.");
    } finally {
      setChecking(false);
    }
  };

  /* Step 2: Final submit after confirmation */
  const confirmSubmit = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      // Re-check just before saving, in case someone registered with this number in the meantime
      const dup = await findDuplicates(values);
      if (applyDuplicates(dup)) return;

      // Call the real backend API
      const response = await fetch(`${API_BASE_URL}/api/register-student`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit registration.");
      }

      const rec = {
        ...values,
        refNo: `AKSD-${values.phone.slice(-4)}`,
        date: new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }),
      };

      setRecord(rec);
      setSubmitted("done");
      toast.success("Registration submitted successfully!");

      try {
        await generatePdf(rec);
      } catch (pdfErr) {
        console.error(pdfErr);
        toast.warning("Registered, but the PDF could not be created. Use the download button.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Could not submit. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const redownload = async () => {
    try {
      await generatePdf(record);
    } catch (err) {
      toast.error("Could not create the PDF.");
    }
  };

  const reset = () => {
    setValues(INITIAL);
    setTouched({});
    setServerErrors({});
    setSubmitted(false);
    setRecord(null);
    setConfirmOpen(false);
  };

  return (
    <div className="ssr-page" data-bs-theme="dark">
      <style>{css}</style>
      <ToastContainer position="top-center" autoClose={3500} theme="dark" />

      {/* Soft animated background */}
      <div className="ssr-bg" aria-hidden="true">
        <span className="ssr-orb ssr-orb-1" />
        <span className="ssr-orb ssr-orb-2" />
        <span className="ssr-orb ssr-orb-3" />
        <span className="ssr-grid" />
      </div>

      {/* Thin progress bar while checking / submitting */}
      {busy && <div className="ssr-topbar" aria-hidden="true" />}

      {/* Hidden inputs */}
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFile} />
      <input ref={captureInputRef} type="file" accept="image/*" capture="user" hidden onChange={handleFile} />

      {/* ---------- Loading overlay ---------- */}
      {busy && (
        <div className="ssr-loader" role="status" aria-live="polite">
          <div className="ssr-loader-box">
            <div className="ssr-spinner" />
            <div className="ssr-loader-text">
              {checking ? "Checking your details…" : "Submitting your registration…"}
            </div>
            <div className="ssr-loader-sub">Please wait a moment</div>
          </div>
        </div>
      )}

      {/* ---------- Camera dialog ---------- */}
      {cameraOpen && (
        <div className="ssr-cam-overlay" role="dialog" aria-modal="true" aria-label="Take your photo">
          <div className="ssr-cam">
            <div className="ssr-cam-head">
              <span>Take your photo</span>
              <button type="button" className="ssr-icon-btn" onClick={closeCamera} aria-label="Close camera">
                <X size={18} />
              </button>
            </div>
            <div className="ssr-cam-view">
              <video ref={videoRef} autoPlay playsInline muted />
              <div className="ssr-cam-guide" />
            </div>
            <p className="ssr-cam-tip">Keep your face inside the frame with good light.</p>
            <div className="ssr-cam-actions">
              <button type="button" className="ssr-btn ssr-btn-outline" onClick={closeCamera}>
                Cancel
              </button>
              <button type="button" className="ssr-btn ssr-btn-primary" onClick={capture}>
                <Camera size={18} /> Capture photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Confirmation popup ---------- */}
      {confirmOpen && (
        <div
          className="ssr-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm your details"
        >
          <div className="ssr-confirm">
            <div className="ssr-confirm-head">
              <div className="ssr-confirm-title">
                <AlertCircle size={22} />
                <span>Please review your details</span>
              </div>
              <button
                type="button"
                className="ssr-icon-btn"
                onClick={() => setConfirmOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <p className="ssr-confirm-sub">
              Make sure everything is correct. If something is wrong, go back and fix it before submitting.
            </p>

            <div className="ssr-confirm-body">
              <div className="ssr-confirm-photo">
                <img src={values.photo} alt="Your photo" />
                <span>Your photo</span>
              </div>

              <div className="ssr-confirm-fields">
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Full name</span>
                  <span className="ssr-confirm-value">{values.name.trim()}</span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Registration no.</span>
                  <span className="ssr-confirm-value">{values.regNo.trim()}</span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Phone number</span>
                  <span className="ssr-confirm-value">+91 {values.phone}</span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Email</span>
                  <span className="ssr-confirm-value">
                    {values.email.trim() || <em className="ssr-muted">Not provided</em>}
                  </span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Semester</span>
                  <span className="ssr-confirm-value">Semester {values.semester}</span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Stream</span>
                  <span className="ssr-confirm-value">{values.stream}</span>
                </div>
                <div className="ssr-confirm-row">
                  <span className="ssr-confirm-label">Knowledge level</span>
                  <span className="ssr-confirm-value">
                    {values.level} / 10{" "}
                    <span className="ssr-level-tag">{levelLabel(values.level)}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="ssr-confirm-note">
              <Info size={16} />
              <span>
                After you submit, your registration PDF will be downloaded automatically and a reference number will be
                generated.
              </span>
            </div>

            <div className="ssr-confirm-actions">
              <button
                type="button"
                className="ssr-btn ssr-btn-outline"
                onClick={() => setConfirmOpen(false)}
              >
                <Pencil size={16} /> Go back and edit
              </button>
              <button
                type="button"
                className="ssr-btn ssr-btn-primary"
                onClick={confirmSubmit}
                disabled={busy}
              >
                <CheckCircle2 size={16} /> {saving ? "Submitting…" : "Confirm and submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Header ---------- */}
      <header className="ssr-header">
        <Container>
          <Row className="align-items-center gy-0">
            <Col xs={6} md={6}>
              <div className="ssr-brand">
                <img src={logo2} alt="AK Software Developers logo" className="ssr-logo ssr-logo-brand" />
                <div>
                  <div className="ssr-brand-name">AK Software Developers</div>
                  <div className="ssr-brand-sub">Software Program Provider</div>
                </div>
              </div>
            </Col>
            <Col xs={6} md={6}>
              <div className="ssr-brand ssr-brand-college justify-content-md-end">
                <img src={satyasailogo} alt="Sri Sathya Sai Institute for Women logo" className="ssr-logo ssr-logo-college" />
                <div className="ssr-college-name">
                  <span className="ssr-full">
                    SRI SATHYA SAI
                    <br />
                    INSTITUTE FOR WOMEN
                  </span>
                  <span className="ssr-short">SSSIOW</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      {/* ---------- Title ---------- */}
      <section className="ssr-hero">
        <Container>
          <h1>
            Skill Development <span>Training Program</span>
          </h1>
          <p>Final year project assistance for students. Choose your stream and register your seat.</p>
        </Container>
      </section>

      {/* ---------- Form ---------- */}
      <Container className="ssr-main">
        <Row className="justify-content-center">
          <Col xs={12} lg={9} xl={8}>
            <div className="ssr-card">
              {submitted === "done" ? (
                <div className="ssr-success">
                  <div className="ssr-success-icon">
                    <CheckCircle2 size={56} />
                  </div>
                  <h2>You’re registered</h2>
                  <p>
                    Thank you, <strong>{record && record.name.trim()}</strong>. We have saved your request for{" "}
                    <strong>{record && record.stream}</strong>. Your registration PDF has been downloaded.
                    Reference no. <strong>{record && record.refNo}</strong>. Please pay the ₹{FEES[0].amount} registration
                    fee to the institute coordinator within 1 week.
                  </p>
                  <div className="ssr-success-actions">
                    <button type="button" className="ssr-btn ssr-btn-primary" onClick={redownload}>
                      <Download size={18} /> Download PDF again
                    </button>
                    <button type="button" className="ssr-btn ssr-btn-outline" onClick={reset}>
                      Register another student
                    </button>
                  </div>
                </div>
              ) : (
                <Form noValidate onSubmit={handleSubmit}>
                  <h2 className="ssr-card-title">Student registration</h2>
                  <p className="ssr-card-sub">Fields marked with * are required.</p>

                  <Row className="g-4">
                    {/* Name */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="name">
                        <Form.Label className="ssr-label">Full name *</Form.Label>
                        <div className={`ssr-input ${show("name") ? "is-bad" : ""}`}>
                          <User size={18} />
                          <Form.Control
                            type="text"
                            placeholder="As on your ID card"
                            value={values.name}
                            maxLength={NAME_MAX}
                            onChange={handleName}
                            onBlur={() => blur("name")}
                            autoComplete="name"
                          />
                        </div>
                        <div className="ssr-field-foot">
                          {show("name") ? <div className="ssr-error">{show("name")}</div> : <span />}
                          <span className={`ssr-counter ${values.name.length >= NAME_MAX ? "max" : ""}`}>
                            {values.name.length}/{NAME_MAX}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    {/* Registration number */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="regNo">
                        <Form.Label className="ssr-label">Registration number *</Form.Label>
                        <div className={`ssr-input ${show("regNo") ? "is-bad" : ""}`}>
                          <Hash size={18} />
                          <Form.Control
                            type="text"
                            placeholder="e.g. U05HB23S0012"
                            value={values.regNo}
                            onChange={(e) => {
                              setField("regNo", e.target.value.toUpperCase());
                              clearServerError("regNo");
                            }}
                            onBlur={() => blur("regNo")}
                          />
                        </div>
                        {show("regNo") && <div className="ssr-error">{show("regNo")}</div>}
                      </Form.Group>
                    </Col>

                    {/* Phone */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="phone">
                        <Form.Label className="ssr-label">Phone number *</Form.Label>
                        <div className={`ssr-input has-prefix ${show("phone") ? "is-bad" : ""}`}>
                          <Phone size={18} />
                          <span className="ssr-prefix">+91</span>
                          <Form.Control
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit mobile number"
                            value={values.phone}
                            onChange={handlePhone}
                            onBlur={() => blur("phone")}
                            autoComplete="tel-national"
                          />
                        </div>
                        {show("phone") && <div className="ssr-error">{show("phone")}</div>}
                      </Form.Group>
                    </Col>

                    {/* Email */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="email">
                        <Form.Label className="ssr-label">
                          Email <span className="ssr-optional">(optional)</span>
                        </Form.Label>
                        <div className={`ssr-input ${show("email") ? "is-bad" : ""}`}>
                          <Mail size={18} />
                          <Form.Control
                            type="email"
                            placeholder="you@example.com"
                            value={values.email}
                            onChange={(e) => {
                              setField("email", e.target.value);
                              clearServerError("email");
                            }}
                            onBlur={() => blur("email")}
                            autoComplete="email"
                          />
                        </div>
                        {show("email") && <div className="ssr-error">{show("email")}</div>}
                      </Form.Group>
                    </Col>

                    {/* Semester */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="semester">
                        <Form.Label className="ssr-label">Semester *</Form.Label>
                        <div className={`ssr-input ${show("semester") ? "is-bad" : ""}`}>
                          <GraduationCap size={18} />
                          <Form.Select
                            value={values.semester}
                            onChange={(e) => setField("semester", e.target.value)}
                            onBlur={() => blur("semester")}
                          >
                            <option value="">Select semester</option>
                            {SEMESTERS.map((s) => (
                              <option key={s} value={s}>
                                Semester {s}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        {show("semester") && <div className="ssr-error">{show("semester")}</div>}
                      </Form.Group>
                    </Col>

                    {/* Stream */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="stream">
                        <Form.Label className="ssr-label">Stream *</Form.Label>
                        <div className={`ssr-input ${show("stream") ? "is-bad" : ""}`}>
                          <Layers size={18} />
                          <Form.Select
                            value={values.stream}
                            onChange={handleStream}
                            onBlur={() => blur("stream")}
                          >
                            <option value="">Select stream</option>
                            {STREAMS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        {show("stream") && <div className="ssr-error">{show("stream")}</div>}
                      </Form.Group>
                    </Col>

                    {/* Knowledge level */}
                    <Col xs={12}>
                      <div className={`ssr-scale-box ${!values.stream ? "is-disabled" : ""}`}>
                        <div className="ssr-scale-head">
                          <div>
                            <div className="ssr-label mb-1">
                              {values.stream
                                ? `How well do you know ${values.stream}? *`
                                : "Knowledge level in your stream *"}
                            </div>
                            <div className="ssr-scale-hint">
                              {values.stream
                                ? "1 means no knowledge, 10 means very confident."
                                : "Select a stream first to rate your knowledge."}
                            </div>
                          </div>
                          <div className="ssr-scale-result">
                            <span className="ssr-scale-number" key={values.level}>
                              {values.level || "–"}
                            </span>
                            <span className="ssr-scale-text">{levelLabel(values.level)}</span>
                          </div>
                        </div>

                        <div className="ssr-scale" role="radiogroup" aria-label="Knowledge level from 1 to 10">
                          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                            <button
                              key={n}
                              type="button"
                              role="radio"
                              aria-checked={values.level === n}
                              disabled={!values.stream}
                              className={`ssr-dot ${values.level >= n ? "on" : ""} ${values.level === n ? "current" : ""}`}
                              onClick={() => {
                                setField("level", n);
                                blur("level");
                              }}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                        {show("level") && <div className="ssr-error mt-2">{show("level")}</div>}
                      </div>
                    </Col>

                    {/* Photo */}
                    <Col xs={12}>
                      <div className={`ssr-photo ${show("photo") ? "is-bad" : ""}`}>
                        <div className="ssr-photo-preview">
                          {values.photo ? (
                            <img src={values.photo} alt="Student preview" />
                          ) : (
                            <div className="ssr-photo-empty">
                              <User size={34} />
                              <span>No photo</span>
                            </div>
                          )}
                        </div>
                        <div className="ssr-photo-body">
                          <div className="ssr-label mb-1">Student photo *</div>
                          <div className="ssr-scale-hint">
                            Face clearly visible with a plain background. Take a new photo or choose one from your phone or computer.
                          </div>
                          <div className="ssr-photo-actions">
                            <button type="button" className="ssr-btn ssr-btn-primary ssr-btn-sm" onClick={openCamera}>
                              <Camera size={16} /> {values.photo ? "Retake photo" : "Take photo"}
                            </button>
                            <button
                              type="button"
                              className="ssr-btn ssr-btn-outline ssr-btn-sm"
                              onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            >
                              <Upload size={16} /> Choose from device
                            </button>
                            {values.photo && (
                              <button
                                type="button"
                                className="ssr-btn ssr-btn-ghost ssr-btn-sm"
                                onClick={() => setField("photo", "")}
                              >
                                <Trash2 size={16} /> Remove
                              </button>
                            )}
                          </div>
                          {show("photo") && <div className="ssr-error mt-2">{show("photo")}</div>}
                        </div>
                      </div>
                    </Col>

                    {/* Fees, terms and declaration */}
                    <Col xs={12}>
                      <div className="ssr-terms">
                        <div className="ssr-fee-strip">
                          <div className="ssr-fee-big">
                            <span className="ssr-fee-total">₹{FEE_TOTAL}</span>
                            <span className="ssr-fee-label">Total fees</span>
                          </div>
                          <p>
                            Pay the ₹{FEES[0].amount} registration fee to the institute coordinator within 1 week of
                            submitting this form.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="ssr-terms-toggle"
                          onClick={() => setTermsOpen((o) => !o)}
                          aria-expanded={termsOpen}
                        >
                          <FileText size={18} />
                          <span>Terms and conditions and fee structure</span>
                          <ChevronDown size={18} className={termsOpen ? "ssr-chev open" : "ssr-chev"} />
                        </button>
                        {termsOpen && (
                          <ol className="ssr-terms-list">
                            {TERMS.map((t) => (
                              <li key={t.title}>
                                <div className="ssr-term-title">
                                  {t.title}
                                  {t.tag && <span className="ssr-term-tag">{t.tag}</span>}
                                </div>
                                <p>{t.text}</p>
                              </li>
                            ))}
                            <li>
                              <div className="ssr-term-title">Fee Structure</div>
                              <ul className="ssr-fee-list">
                                {FEES.map((f) => (
                                  <li key={f.label}>
                                    <span>{f.label}</span>
                                    <strong>₹{f.amount}</strong>
                                  </li>
                                ))}
                                <li className="total">
                                  <span>Total fees</span>
                                  <strong>₹{FEE_TOTAL}</strong>
                                </li>
                              </ul>
                            </li>
                          </ol>
                        )}
                      </div>
                      <p className="ssr-declare">
                        {DECLARATION} By clicking "Submit and download PDF", I agree to this declaration and to the
                        terms and conditions above.
                      </p>
                    </Col>
                  </Row>

                  <div className="ssr-actions">
                    <button type="button" className="ssr-btn ssr-btn-outline" onClick={reset} disabled={busy}>
                      Clear form
                    </button>
                    <button type="submit" className="ssr-btn ssr-btn-primary ssr-btn-shine" disabled={busy}>
                      <Send size={18} />
                      {checking ? "Checking…" : saving ? "Submitting…" : "Submit and download PDF"}
                    </button>
                  </div>
                </Form>
              )}
            </div>

            <p className="ssr-footer">
              © {new Date().getFullYear()} AK Software Developers · Sri Sathya Sai Institute for Women
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

/* ---------------- Internal CSS ---------------- */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.ssr-page {
  --bg: #07080b;
  --surface: #0d0f14;
  --surface-2: #11141b;
  --field: #090b10;
  --line: #1c212c;
  --line-2: #2a3140;
  --text: #ffffff;
  --body: #b4bac7;
  --muted: #8a93a6;
  --blue: #2563eb;
  --blue-2: #3b82f6;
  --blue-glow: rgba(59, 130, 246, 0.35);
  --amber: #fbbf24;
  --green: #22c55e;
  --bad: #f87171;
  position: relative;
  overflow-x: hidden;
  min-height: 100vh;
  color: var(--body);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  background-color: var(--bg);
  background-image:
    radial-gradient(60% 40% at 50% 0%, rgba(37, 99, 235, 0.20), transparent 70%),
    radial-gradient(circle at 12% 34%, rgba(59, 130, 246, 0.9) 0 2px, transparent 3px),
    radial-gradient(circle at 24% 52%, rgba(59, 130, 246, 0.7) 0 2px, transparent 3px),
    radial-gradient(circle at 88% 28%, rgba(59, 130, 246, 0.6) 0 2px, transparent 3px);
}

/* ---------- Animated background (mild, professional) ---------- */
.ssr-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}
.ssr-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  will-change: transform;
}
.ssr-orb-1 {
  width: 460px; height: 460px;
  top: -140px; left: -120px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.55), transparent 70%);
  animation: ssr-float-1 22s ease-in-out infinite;
}
.ssr-orb-2 {
  width: 520px; height: 520px;
  bottom: -180px; right: -140px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.32), transparent 70%);
  animation: ssr-float-2 28s ease-in-out infinite;
}
.ssr-orb-3 {
  width: 340px; height: 340px;
  top: 42%; left: 58%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.30), transparent 70%);
  animation: ssr-float-3 26s ease-in-out infinite;
}
.ssr-grid {
  position: absolute;
  inset: -60px;
  background-image:
    linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px);
  background-size: 56px 56px;
  -webkit-mask-image: radial-gradient(70% 60% at 50% 30%, #000 30%, transparent 100%);
  mask-image: radial-gradient(70% 60% at 50% 30%, #000 30%, transparent 100%);
  animation: ssr-grid-drift 40s linear infinite;
}
@keyframes ssr-float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(90px, 70px) scale(1.12); }
}
@keyframes ssr-float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-100px, -60px) scale(1.1); }
}
@keyframes ssr-float-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-70px, 60px) scale(0.9); }
}
@keyframes ssr-grid-drift {
  from { transform: translate(0, 0); }
  to { transform: translate(56px, 56px); }
}

/* Keep real content above the background layer */
.ssr-header, .ssr-hero, .ssr-main { position: relative; z-index: 1; }

/* Entrance animations */
@keyframes ssr-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ssr-fade-down {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.ssr-header { animation: ssr-fade-down 0.6s ease-out both; }
.ssr-hero h1 { animation: ssr-fade-up 0.7s 0.1s ease-out both; }
.ssr-hero p { animation: ssr-fade-up 0.7s 0.25s ease-out both; }
.ssr-card { animation: ssr-fade-up 0.8s 0.35s ease-out both; }
.ssr-footer { animation: ssr-fade-up 0.8s 0.6s ease-out both; }

/* Header */
.ssr-header {
  background: rgba(7, 8, 11, 0.85);
  border-bottom: 1px solid var(--line);
  padding: 14px 0;
  backdrop-filter: blur(8px);
}
.ssr-brand { display: flex; align-items: center; gap: 14px; }
.ssr-logo { height: 60px; width: auto; max-width: 150px; object-fit: contain; }
.ssr-logo-brand { border-radius: 12px; }
.ssr-logo-college {
  height: 62px; width: 62px; border-radius: 50%;
  border: 2px solid var(--blue-2); padding: 2px; background: #fff;
  box-shadow: 0 0 18px var(--blue-glow);
  animation: ssr-glow 4s ease-in-out infinite;
}
@keyframes ssr-glow {
  0%, 100% { box-shadow: 0 0 14px rgba(59, 130, 246, 0.28); }
  50% { box-shadow: 0 0 26px rgba(59, 130, 246, 0.55); }
}
.ssr-brand-name { font-size: 1.25rem; font-weight: 700; color: var(--text); line-height: 1.15; }
.ssr-brand-sub { font-size: 0.7rem; font-weight: 500; color: var(--muted); letter-spacing: 0.3em; margin-top: 4px; text-transform: uppercase; }
.ssr-college-name { font-size: 0.95rem; font-weight: 700; color: var(--text); line-height: 1.3; letter-spacing: 0.03em; }
.ssr-short { display: none; }

/* Hero */
.ssr-hero { text-align: center; padding: 56px 0 84px; }
.ssr-hero h1 { font-size: clamp(2rem, 5.5vw, 3.6rem); font-weight: 800; color: var(--text); margin: 0 0 14px; letter-spacing: -0.03em; line-height: 1.1; }
.ssr-hero h1 span {
  background: linear-gradient(90deg, #3b82f6, #60a5fa, #38bdf8, #3b82f6);
  background-size: 250% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--blue-2);
  animation: ssr-text-flow 8s ease-in-out infinite;
}
@keyframes ssr-text-flow {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.ssr-hero p { margin: 0 auto; max-width: 560px; color: var(--body); font-size: 1.05rem; line-height: 1.65; }

/* Card */
.ssr-main { margin-top: -40px; padding-bottom: 32px; }
.ssr-card {
  background: rgba(13, 15, 20, 0.92);
  border: 1px solid var(--line);
  border-radius: 22px;
  padding: 38px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(59, 130, 246, 0.04);
  backdrop-filter: blur(6px);
}
.ssr-card-title { font-size: 1.45rem; font-weight: 700; margin: 0; color: var(--text); letter-spacing: -0.01em; }
.ssr-card-sub { margin: 4px 0 28px; color: var(--muted); font-size: 0.92rem; }

/* Fields */
.ssr-label { font-size: 0.86rem; font-weight: 600; color: #e6e9f0; margin-bottom: 8px; }
.ssr-optional { font-weight: 500; color: var(--muted); }
.ssr-input {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--field);
  border: 1px solid var(--line-2);
  border-radius: 12px;
  color: var(--muted);
  transition: border-color 0.15s, box-shadow 0.15s, color 0.15s;
}
.ssr-input > svg:first-child {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.ssr-input:focus-within {
  border-color: var(--blue-2);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.16);
  color: var(--blue-2);
}
.ssr-input.is-bad { border-color: var(--bad); color: var(--bad); box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.12); }
.ssr-input .form-control,
.ssr-input .form-select {
  width: 100%;
  border: 0;
  background-color: transparent;
  box-shadow: none !important;
  padding: 14px 16px 14px 48px;
  font-size: 0.95rem;
  color: var(--text);
  font-family: inherit;
}
.ssr-input .form-select { padding-right: 2.75rem; cursor: pointer; }
.ssr-input .form-select option { background: var(--surface-2); color: var(--text); padding: 10px 16px; }
.ssr-input .form-control::placeholder { color: #5d6579; }
.ssr-input input:-webkit-autofill {
  -webkit-text-fill-color: var(--text);
  -webkit-box-shadow: 0 0 0 40px var(--field) inset;
}
.ssr-prefix {
  position: absolute;
  left: 48px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  font-size: 0.92rem;
  line-height: 1.4;
  color: var(--text);
  padding-right: 12px;
  border-right: 1px solid var(--line-2);
  pointer-events: none;
}
.ssr-input.has-prefix .form-control { padding-left: 104px; }
.ssr-error { color: var(--bad); font-size: 0.8rem; font-weight: 500; margin-top: 7px; animation: ssr-fade-down 0.25s ease-out both; }
.ssr-field-foot { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.ssr-counter { margin-top: 7px; font-size: 0.74rem; font-weight: 600; color: var(--muted); flex-shrink: 0; margin-left: auto; transition: color 0.15s; }
.ssr-counter.max { color: var(--amber); }
.ssr-note {
  display: flex; align-items: center; gap: 6px;
  margin-top: 8px; padding: 8px 11px;
  background: rgba(251, 191, 36, 0.08); border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px; font-size: 0.8rem; font-weight: 500; color: var(--amber);
  animation: ssr-fade-down 0.25s ease-out both;
}

/* Knowledge scale */
.ssr-scale-box {
  border: 1px solid var(--line-2);
  border-radius: 16px;
  padding: 22px;
  background: linear-gradient(180deg, var(--surface-2), var(--surface));
  transition: opacity 0.2s;
}
.ssr-scale-box.is-disabled { opacity: 0.55; }
.ssr-scale-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; }
.ssr-scale-hint { font-size: 0.82rem; color: var(--muted); line-height: 1.5; }
.ssr-scale-result { text-align: right; flex-shrink: 0; }
.ssr-scale-number { display: block; font-size: 2.2rem; font-weight: 800; color: var(--blue-2); line-height: 1; text-shadow: 0 0 24px var(--blue-glow); animation: ssr-bump 0.3s ease-out; }
@keyframes ssr-bump {
  0% { transform: scale(0.8); opacity: 0.5; }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); opacity: 1; }
}
.ssr-scale-text { font-size: 0.78rem; font-weight: 600; color: var(--amber); }
.ssr-scale { display: grid; grid-template-columns: repeat(10, 1fr); gap: 8px; }
.ssr-dot {
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  border: 1px solid var(--line-2);
  background: var(--field);
  color: var(--muted);
  font-weight: 700;
  font-size: 0.95rem;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s;
}
.ssr-dot:hover:not(:disabled) { border-color: var(--blue-2); color: var(--text); transform: translateY(-2px); }
.ssr-dot.on { background: rgba(37, 99, 235, 0.28); border-color: rgba(59, 130, 246, 0.6); color: #dbe7ff; }
.ssr-dot.current { background: var(--blue); border-color: var(--blue-2); color: #fff; transform: scale(1.08); box-shadow: 0 0 20px var(--blue-glow); }
.ssr-dot:disabled { cursor: not-allowed; }
.ssr-dot:focus-visible, .ssr-btn:focus-visible, .ssr-icon-btn:focus-visible { outline: 2px solid var(--blue-2); outline-offset: 3px; }

/* Photo */
.ssr-photo {
  display: flex;
  gap: 22px;
  align-items: center;
  border: 1px solid var(--line-2);
  border-radius: 16px;
  padding: 20px;
  background: linear-gradient(180deg, var(--surface-2), var(--surface));
}
.ssr-photo.is-bad { border-color: var(--bad); box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.12); }
.ssr-photo-preview {
  width: 112px;
  aspect-ratio: 3 / 4;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px dashed var(--line-2);
  background: var(--field);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ssr-photo-preview img { width: 100%; height: 100%; object-fit: cover; display: block; border: 0; animation: ssr-fade-up 0.4s ease-out both; }
.ssr-photo-preview:has(img) { border: 2px solid var(--blue-2); box-shadow: 0 0 20px var(--blue-glow); }
.ssr-photo-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; color: var(--muted); font-size: 0.75rem; font-weight: 500; }
.ssr-photo-body { flex: 1; min-width: 0; }
.ssr-photo-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }

/* Fees, terms, declaration */
.ssr-terms { border: 1px solid var(--line-2); border-radius: 16px; overflow: hidden; background: linear-gradient(180deg, var(--surface-2), var(--surface)); }
.ssr-fee-strip { display: flex; align-items: center; gap: 18px; padding: 18px 20px; border-bottom: 1px solid var(--line); }
.ssr-fee-strip p { margin: 0; font-size: 0.88rem; line-height: 1.55; color: var(--body); }
.ssr-fee-big { display: flex; flex-direction: column; flex-shrink: 0; padding-right: 18px; border-right: 1px solid var(--line-2); }
.ssr-fee-total { font-size: 1.8rem; font-weight: 800; color: var(--blue-2); line-height: 1; text-shadow: 0 0 22px var(--blue-glow); }
.ssr-fee-label { font-size: 0.75rem; font-weight: 600; color: var(--muted); margin-top: 4px; }
.ssr-terms-toggle {
  width: 100%; display: flex; align-items: center; gap: 10px;
  background: transparent; border: 0; color: var(--text);
  padding: 15px 20px; font-family: inherit; font-weight: 600; font-size: 0.92rem; text-align: left; cursor: pointer;
}
.ssr-terms-toggle span { flex: 1; }
.ssr-terms-toggle:hover { background: rgba(59, 130, 246, 0.06); }
.ssr-terms-toggle:focus-visible { outline: 2px solid var(--blue-2); outline-offset: -2px; }
.ssr-chev { transition: transform 0.2s; color: var(--muted); }
.ssr-chev.open { transform: rotate(180deg); }
.ssr-terms-list { margin: 0; padding: 4px 20px 20px 40px; border-top: 1px solid var(--line); color: var(--body); animation: ssr-fade-down 0.3s ease-out both; }
.ssr-terms-list > li { padding: 14px 0 0 4px; }
.ssr-terms-list > li::marker { color: var(--blue-2); font-weight: 700; }
.ssr-term-title { font-weight: 700; color: var(--text); font-size: 0.92rem; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ssr-term-tag { font-size: 0.7rem; font-weight: 600; color: var(--amber); background: rgba(251, 191, 36, 0.1); border: 1px solid rgba(251, 191, 36, 0.3); border-radius: 999px; padding: 2px 9px; }
.ssr-terms-list p { margin: 4px 0 0; font-size: 0.86rem; line-height: 1.6; }
.ssr-fee-list { list-style: none; margin: 8px 0 0; padding: 0; border: 1px solid var(--line-2); border-radius: 10px; overflow: hidden; }
.ssr-fee-list li { display: flex; justify-content: space-between; gap: 12px; padding: 9px 14px; font-size: 0.86rem; border-bottom: 1px solid var(--line); }
.ssr-fee-list li:last-child { border-bottom: 0; }
.ssr-fee-list strong { color: var(--text); white-space: nowrap; }
.ssr-fee-list li.total { background: rgba(37, 99, 235, 0.14); color: var(--text); font-weight: 700; }
.ssr-fee-list li.total strong { color: var(--blue-2); }
.ssr-declare { margin: 14px 2px 0; font-size: 0.84rem; line-height: 1.6; color: var(--muted); }

/* ---------- Loading: top bar + overlay ---------- */
.ssr-topbar {
  position: fixed; top: 0; left: 0; right: 0; height: 3px; z-index: 2300;
  background: rgba(59, 130, 246, 0.15);
  overflow: hidden;
}
.ssr-topbar::after {
  content: "";
  position: absolute; top: 0; bottom: 0; width: 35%;
  background: linear-gradient(90deg, transparent, var(--blue-2), #38bdf8, transparent);
  animation: ssr-topbar 1.2s ease-in-out infinite;
}
@keyframes ssr-topbar {
  from { left: -35%; }
  to { left: 100%; }
}
.ssr-loader {
  position: fixed; inset: 0; z-index: 2250;
  background: rgba(3, 4, 6, 0.6);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: ssr-fade-in 0.2s ease-out both;
}
@keyframes ssr-fade-in { from { opacity: 0; } to { opacity: 1; } }
.ssr-loader-box {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 28px 38px;
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(59, 130, 246, 0.12);
  animation: ssr-fade-up 0.3s ease-out both;
}
.ssr-spinner {
  width: 46px; height: 46px; border-radius: 50%;
  border: 3px solid rgba(59, 130, 246, 0.18);
  border-top-color: var(--blue-2);
  border-right-color: #38bdf8;
  animation: ssr-spin 0.9s linear infinite;
  margin-bottom: 6px;
}
@keyframes ssr-spin { to { transform: rotate(360deg); } }
.ssr-loader-text { color: var(--text); font-weight: 600; font-size: 0.98rem; }
.ssr-loader-sub { color: var(--muted); font-size: 0.8rem; }

/* Camera dialog */
.ssr-cam-overlay {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(3, 4, 6, 0.82);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: ssr-fade-in 0.2s ease-out both;
}
.ssr-cam {
  width: 100%; max-width: 460px;
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7);
  animation: ssr-pop 0.2s ease-out;
}
.ssr-cam-head { display: flex; justify-content: space-between; align-items: center; color: var(--text); font-weight: 700; margin-bottom: 14px; }
.ssr-icon-btn { background: transparent; border: 1px solid var(--line-2); color: var(--text); width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.15s, transform 0.15s; }
.ssr-icon-btn:hover { border-color: var(--blue-2); transform: rotate(90deg); }
.ssr-cam-view { position: relative; aspect-ratio: 3 / 4; border-radius: 14px; overflow: hidden; background: #000; }
.ssr-cam-view video { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); display: block; }
.ssr-cam-guide { position: absolute; inset: 12% 18%; border: 2px dashed rgba(59, 130, 246, 0.8); border-radius: 50% / 45%; pointer-events: none; animation: ssr-guide 2.6s ease-in-out infinite; }
@keyframes ssr-guide {
  0%, 100% { border-color: rgba(59, 130, 246, 0.45); }
  50% { border-color: rgba(59, 130, 246, 0.95); }
}
.ssr-cam-tip { text-align: center; font-size: 0.82rem; color: var(--muted); margin: 12px 0; }
.ssr-cam-actions { display: flex; gap: 10px; justify-content: flex-end; }

/* Confirmation popup */
.ssr-confirm-overlay {
  position: fixed; inset: 0; z-index: 2100;
  background: rgba(3, 4, 6, 0.85);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  overflow-y: auto;
  animation: ssr-fade-in 0.2s ease-out both;
}
.ssr-confirm {
  width: 100%; max-width: 540px;
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: 20px;
  padding: 22px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.75);
  animation: ssr-pop 0.2s ease-out;
  max-height: 92vh;
  overflow-y: auto;
}
@keyframes ssr-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.ssr-confirm-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.ssr-confirm-title { display: flex; align-items: center; gap: 10px; color: var(--amber); font-weight: 700; font-size: 1.05rem; }
.ssr-confirm-sub { font-size: 0.85rem; color: var(--muted); margin: 0 0 16px; line-height: 1.55; }
.ssr-confirm-body { display: flex; gap: 18px; align-items: flex-start; }
.ssr-confirm-photo {
  flex-shrink: 0;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.ssr-confirm-photo img {
  width: 100px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 10px;
  border: 2px solid var(--blue-2);
  box-shadow: 0 0 18px var(--blue-glow);
  display: block;
}
.ssr-confirm-photo span { font-size: 0.72rem; font-weight: 600; color: var(--muted); }
.ssr-confirm-fields { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 8px; }
.ssr-confirm-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 10px;
  border-radius: 8px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  font-size: 0.85rem;
  animation: ssr-fade-up 0.35s ease-out both;
}
.ssr-confirm-row:nth-child(1) { animation-delay: 0.04s; }
.ssr-confirm-row:nth-child(2) { animation-delay: 0.08s; }
.ssr-confirm-row:nth-child(3) { animation-delay: 0.12s; }
.ssr-confirm-row:nth-child(4) { animation-delay: 0.16s; }
.ssr-confirm-row:nth-child(5) { animation-delay: 0.20s; }
.ssr-confirm-row:nth-child(6) { animation-delay: 0.24s; }
.ssr-confirm-row:nth-child(7) { animation-delay: 0.28s; }
.ssr-confirm-label { color: var(--muted); font-weight: 500; flex-shrink: 0; }
.ssr-confirm-value { color: var(--text); font-weight: 600; text-align: right; word-break: break-word; }
.ssr-muted { color: var(--muted); font-style: italic; font-weight: 500; }
.ssr-level-tag {
  display: inline-block;
  margin-left: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--amber);
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 999px;
  padding: 1px 8px;
}
.ssr-confirm-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 11px 13px;
  background: rgba(37, 99, 235, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 10px;
  color: #c8d8fb;
  font-size: 0.82rem;
  line-height: 1.55;
}
.ssr-confirm-note svg { flex-shrink: 0; margin-top: 2px; }
.ssr-confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}

/* Buttons */
.ssr-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--line); }
.ssr-btn {
  position: relative; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border-radius: 12px; padding: 13px 28px;
  font-weight: 600; font-size: 0.95rem; font-family: inherit;
  cursor: pointer; transition: background 0.15s, box-shadow 0.15s, transform 0.1s, border-color 0.15s;
}
.ssr-btn-sm { padding: 9px 16px; font-size: 0.86rem; border-radius: 10px; }
.ssr-btn-primary { background: var(--blue); color: #fff; border: 1px solid var(--blue); }
.ssr-btn-primary:hover:not(:disabled) { background: var(--blue-2); border-color: var(--blue-2); box-shadow: 0 10px 28px var(--blue-glow); transform: translateY(-1px); }
.ssr-btn-primary:active:not(:disabled) { transform: translateY(1px); }
.ssr-btn-primary:disabled { opacity: 0.7; cursor: wait; }
.ssr-btn-outline { background: transparent; color: var(--text); border: 1px solid var(--line-2); }
.ssr-btn-outline:hover:not(:disabled) { border-color: var(--blue-2); background: rgba(59, 130, 246, 0.08); }
.ssr-btn-outline:disabled { opacity: 0.55; cursor: not-allowed; }
.ssr-btn-ghost { background: transparent; color: var(--bad); border: 1px solid transparent; }
.ssr-btn-ghost:hover { background: rgba(248, 113, 113, 0.1); }

/* Soft shine sweeping across the main submit button */
.ssr-btn-shine::after {
  content: "";
  position: absolute; top: 0; bottom: 0; left: -60%; width: 40%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.22), transparent);
  transform: skewX(-20deg);
  animation: ssr-shine 4.5s ease-in-out infinite;
  pointer-events: none;
}
.ssr-btn-shine:disabled::after { animation: none; opacity: 0; }
@keyframes ssr-shine {
  0%, 60% { left: -60%; }
  100% { left: 130%; }
}

/* Success */
.ssr-success { text-align: center; padding: 30px 8px; color: var(--green); }
.ssr-success-icon { display: inline-block; animation: ssr-success-pop 0.6s cubic-bezier(0.2, 1.2, 0.4, 1) both; filter: drop-shadow(0 0 18px rgba(34, 197, 94, 0.45)); }
@keyframes ssr-success-pop {
  from { opacity: 0; transform: scale(0.4) rotate(-20deg); }
  to { opacity: 1; transform: scale(1) rotate(0); }
}
.ssr-success h2 { color: var(--text); font-weight: 700; margin: 16px 0 8px; animation: ssr-fade-up 0.5s 0.2s ease-out both; }
.ssr-success p { color: var(--body); max-width: 480px; margin: 0 auto 26px; line-height: 1.65; animation: ssr-fade-up 0.5s 0.3s ease-out both; }
.ssr-success strong { color: var(--text); }
.ssr-success-actions { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; animation: ssr-fade-up 0.5s 0.4s ease-out both; }

.ssr-footer { text-align: center; color: var(--muted); font-size: 0.82rem; margin: 22px 0 0; }

/* Responsive */
@media (max-width: 767.98px) {
  .ssr-header { padding: 10px 0; }
  .ssr-brand { flex-direction: column; gap: 5px; text-align: center; }
  .ssr-brand-college { justify-content: center; }
  .ssr-logo { height: 38px; }
  .ssr-logo-college { height: 42px; width: 42px; border-width: 1.5px; }
  .ssr-brand-name { font-size: 0.68rem; font-weight: 600; color: var(--body); }
  .ssr-brand-sub { display: none; }
  .ssr-college-name { font-size: 0.68rem; font-weight: 600; color: var(--body); letter-spacing: 0.08em; }
  .ssr-full { display: none; }
  .ssr-short { display: inline; }
  .ssr-hero { padding: 36px 0 70px; }
  .ssr-orb { filter: blur(54px); }
}
@media (max-width: 575.98px) {
  .ssr-card { padding: 24px 16px; border-radius: 18px; }
  .ssr-scale { grid-template-columns: repeat(5, 1fr); }
  .ssr-scale-head { flex-direction: column; }
  .ssr-scale-result { text-align: left; display: flex; align-items: baseline; gap: 10px; }
  .ssr-photo { flex-direction: column; text-align: center; }
  .ssr-fee-strip { flex-direction: column; align-items: flex-start; gap: 12px; }
  .ssr-fee-big { flex-direction: row; align-items: baseline; gap: 10px; padding-right: 0; border-right: 0; }
  .ssr-terms-list { padding-left: 34px; padding-right: 14px; }
  .ssr-photo-actions { justify-content: center; }
  .ssr-actions { flex-direction: column-reverse; }
  .ssr-actions .ssr-btn, .ssr-success-actions .ssr-btn { width: 100%; }
  .ssr-confirm-body { flex-direction: column; align-items: center; }
  .ssr-confirm-photo { width: 90px; }
  .ssr-confirm-photo img { width: 90px; }
  .ssr-confirm-actions { flex-direction: column-reverse; }
  .ssr-confirm-actions .ssr-btn { width: 100%; }
}

/* Respect users who prefer less motion */
@media (prefers-reduced-motion: reduce) {
  .ssr-page *, .ssr-page *::before, .ssr-page *::after {
    animation: none !important;
    transition: none !important;
  }
  .ssr-spinner { animation: ssr-spin 1.6s linear infinite !important; }
}
`;